import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Building2, Loader2, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { claveDeErrorDeCanje, invitaciones, leerCodigoOEnlace } from "@/lib/peritoInvitaciones";
import DeleteAccountDialog from "@/components/settings/DeleteAccountDialog";

/**
 * El perito que se ha quedado SIN EMPRESA (P3 = A, Pablo 26-09-2026).
 *
 * Su única empresa le quitó («Eliminar» ya no borra la cuenta de un perito: le
 * desvincula). La cuenta sigue, sin empresa ni rol, para que otro cliente pueda
 * invitarle. Antes, al entrar, se le echaba con «No company associated with your
 * account» o veía «No se ha podido cargar tu cuenta». Ahora se le dice qué pasa y
 * puede pegar el ENLACE que le manden o el CÓDIGO; al acertar, entra en su portal.
 * También puede pedir que se borre su cuenta (D7).
 */
export function PeritoSinEmpresa() {
  const { t } = useTranslation();
  const { refreshProfile, signOut } = useAuth();
  const [texto, setTexto] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [borrarCuenta, setBorrarCuenta] = useState(false);

  const vincular = async () => {
    const leido = leerCodigoOEnlace(texto);
    if (!leido) return;
    setCargando(true);
    setError(null);
    try {
      const { data, error: rpcError } =
        leido.tipo === "enlace" ? await invitaciones.canjearEnlace(leido.token) : await invitaciones.canjearCodigo(leido.codigo);
      if (rpcError) throw rpcError;
      if (!data || data.status !== "ok") {
        setError(t(claveDeErrorDeCanje(data?.error), { empresa: data?.company_name ?? "" }));
        return;
      }
      // Ya es perito de esa empresa: al recargar el perfil, el enrutador le lleva a su portal.
      await refreshProfile();
    } catch (err) {
      console.error("[PeritoSinEmpresa] canje:", err);
      setError(t("perito.multiCompany.linkError"));
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4 py-8" style={{ background: "var(--bg-page)" }}>
      <div
        className="w-full max-w-md space-y-5 rounded-2xl border border-[#E4EDF5] bg-white p-6 shadow-sm"
        data-testid="perito-sin-empresa"
      >
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
            <Building2 className="h-6 w-6 text-muted-foreground" />
          </div>
          <h1 className="text-xl font-bold text-foreground">{t("perito.sinEmpresa.titulo")}</h1>
          <p className="text-sm text-muted-foreground">{t("perito.sinEmpresa.texto")}</p>
        </div>

        <div className="space-y-2">
          <label htmlFor="perito-sin-empresa-codigo" className="text-sm font-semibold text-foreground">
            {t("perito.sinEmpresa.campo")}
          </label>
          <Input
            id="perito-sin-empresa-codigo"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              setError(null);
            }}
            placeholder={t("perito.sinEmpresa.placeholder")}
            autoComplete="off"
            autoCapitalize="none"
            aria-invalid={!!error}
            aria-describedby={error ? "perito-sin-empresa-error" : undefined}
            disabled={cargando}
            onKeyDown={(e) => e.key === "Enter" && void vincular()}
            data-testid="perito-sin-empresa-input"
          />
          {error && (
            <p id="perito-sin-empresa-error" role="alert" className="flex items-start gap-1.5 text-[13px] text-red-600" data-testid="perito-sin-empresa-error">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </p>
          )}
          <Button className="w-full" onClick={() => void vincular()} disabled={cargando || !leerCodigoOEnlace(texto)}>
            {cargando ? <Loader2 className="h-4 w-4 animate-spin" /> : t("perito.sinEmpresa.vincular")}
          </Button>
        </div>

        <div className="flex flex-col gap-2 border-t border-[#E4EDF5] pt-4">
          <Button variant="outline" className="w-full gap-2" onClick={() => void signOut()}>
            <LogOut className="h-4 w-4" />
            {t("auth.signOut")}
          </Button>
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-destructive"
            onClick={() => setBorrarCuenta(true)}
          >
            {t("settings.account.deleteButton")}
          </button>
        </div>
      </div>
      <DeleteAccountDialog open={borrarCuenta} onOpenChange={setBorrarCuenta} />
    </div>
  );
}
